import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { categoryPath, cityPath, cityPathSlug, cityDisplayName } from '../utils/browsePaths';
import {
    Container,
    Grid,
    Typography,
    Card,
    CardContent,
    CardMedia,
    Box,
    Chip,
    CircularProgress,
    Button,
    Avatar,
    Rating,
    TextField,
    MenuItem,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import MapIcon from '@mui/icons-material/Map';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewCompactIcon from '@mui/icons-material/ViewCompact';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow, Libraries, MarkerClusterer } from '@react-google-maps/api';

import { businessService } from '../services/businessService';
import { cityService } from '../services/cityService';
import { categoryService } from '../services/categoryService';
import { Business } from '../types/business';
import { Category } from '../types/category';
import { City } from '../types/city';
import BusinessCard from '../components/common/BusinessCard';
import { GOOGLE_MAPS_API_KEY } from '../config/googleMaps';
import useSEO from '../hooks/useSEO';
import { API_ORIGIN } from '../config/api';

const libraries: Libraries = ['places'];
const SYDNEY_CENTER = { lat: -33.8688, lng: 151.2093 };

const clusterStyles = [
    {
        textColor: 'white',
        url: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI1MCIgaGVpZ2h0PSI1MCI+PGNpcmNsZSBjeD0iMjUiIGN5PSIyNSIgcj0iMjEiIGZpbGw9IiMyODU0YTEiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMiIvPjxjaXJjbGUgY3g9IjI1IiBjeT0iMjUiIHI9IjI0IiBmaWxsPSJub25lIiBzdHJva2U9IiMyODU0YTEiIHN0cm9rZS13aWR0aD0iMSIgc3Ryb2tlLW9wYWNpdHk9IjAuNSIvPjwvc3ZnPg==',
        height: 50,
        width: 50,
        textSize: 16,
        fontWeight: 'bold'
    },
    {
        textColor: 'white',
        url: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMjUiIGZpbGw9IiMyODU0YTEiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMiIvPjxjaXJjbGUgY3g9IjMwIiBjeT0iMzAiIHI9IjI5IiBmaWxsPSJub25lIiBzdHJva2U9IiMyODU0YTEiIHN0cm9rZS13aWR0aD0iMSIgc3Ryb2tlLW9wYWNpdHk9IjAuNSIvPjwvc3ZnPg==',
        height: 60,
        width: 60,
        textSize: 18,
        fontWeight: 'bold'
    }
];



const PAGE_SIZE = 24;

const SearchResultsPage: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    // Set on the readable browse addresses: /category/<slug> and /city/<slug>.
    const { categorySlug, citySlug } = useParams<{ categorySlug?: string; citySlug?: string }>();
    const [businesses, setBusinesses] = useState<Business[]>([]);
    const [loading, setLoading] = useState(true);
    const [categories, setCategories] = useState<Category[]>([]);
    const [cities, setCities] = useState<City[]>([]);

    // Search fields state
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedCity, setSelectedCity] = useState('');
    // Checked by default; unchecking adds ?includeIran=1 to the URL so it survives reloads/sharing.
    const [excludeIran, setExcludeIran] = useState(true);

    // View mode state
    const [viewMode, setViewMode] = useState<'list' | 'map' | 'both'>('list');
    // List view shows results a page at a time ("show more") instead of
    // rendering several hundred cards at once, which was slow on phones.
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null);
    const [map, setMap] = useState<google.maps.Map | null>(null);
    const [center, setCenter] = useState(SYDNEY_CENTER);
    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        libraries
    });

    const mapCenter = useMemo(() => {
        if (businesses.length > 0 && businesses[0].latitude != null && businesses[0].longitude != null) {
            return { lat: Number(businesses[0].latitude), lng: Number(businesses[0].longitude) };
        }
        return SYDNEY_CENTER;
    }, [businesses]);

    const getMarkerIcon = useCallback((isSelected: boolean) => {
        if (!isLoaded) return undefined;
        // Blue/Premium color for selected, grey for others
        const color = isSelected ? '#2854a1' : '#555';
        const innerColor = isSelected ? '#ffffff' : '#ffffff';
        const textColor = isSelected ? '#2854a1' : '#555';

        return {
            url: `data:image/svg+xml;base64,${btoa(`<svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42">
                <path fill="${color}" d="M16 0C7.2 0 0 7.2 0 16c0 10.5 16 26 16 26s16-15.5 16-26C32 7.2 24.8 0 16 0z"/>
                <circle fill="${innerColor}" cx="16" cy="16" r="10"/>
                <text x="16" y="22" font-family="Arial" font-size="14" font-weight="bold" text-anchor="middle" fill="${textColor}">i</text>
            </svg>`)}`,
            scaledSize: new google.maps.Size(isSelected ? 40 : 32, isSelected ? 52 : 42),
            anchor: new google.maps.Point(isSelected ? 20 : 16, isSelected ? 52 : 42)
        };
    }, [isLoaded]);

    const markerIcon = useMemo(() => getMarkerIcon(false), [getMarkerIcon]);
    const selectedMarkerIcon = useMemo(() => getMarkerIcon(true), [getMarkerIcon]);

    // Update center when mapCenter (initial load) changes
    useEffect(() => {
        setCenter(mapCenter);
    }, [mapCenter]);

    useEffect(() => {
        const fetchFilters = async () => {
            try {
                const [cats, cits] = await Promise.all([
                    categoryService.getAll(),
                    cityService.getAll()
                ]);
                setCategories(cats);
                setCities(cits);
            } catch (error) {
                console.error('Failed to fetch filter options', error);
            }
        };
        fetchFilters();
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        setSearchQuery(params.get('q') || '');
        // On /category/<slug> and /city/<slug> the dropdowns are filled in by the
        // slug-resolving effect below, once the category/city lists have loaded.
        if (!categorySlug) setSelectedCategory(params.get('categoryId') || '');
        if (!citySlug) setSelectedCity(params.get('cityId') || '');
        setExcludeIran(params.get('includeIran') !== '1');

        setVisibleCount(PAGE_SIZE);
        const fetchResults = async () => {
            setLoading(true);
            try {
                const params = new URLSearchParams(location.search);
                const categoryId = params.get('categoryId') || undefined;
                const cityId = params.get('cityId') || undefined;
                const q = params.get('q') || undefined;

                const data = await businessService.getAll(cityId, categoryId, q, undefined, params.get('includeIran') !== '1', categorySlug, citySlug);
                setBusinesses(data);
            } catch (error) {
                console.error('Failed to fetch search results', error);
            } finally {
                setLoading(false);
            }
        };

        fetchResults();
    }, [location.search, categorySlug, citySlug]);

    // Businesses that can actually be plotted on the map (no lat/lng = no pin).
    // "Found N results" counts everything matching the search, which can be higher
    // than what's shown on the map — surfaced separately so the two numbers don't
    // look like a bug.
    const mappableBusinesses = useMemo(
        () => businesses.filter(b => b.latitude != null && b.longitude != null),
        [businesses]
    );

    // Shared bounds-fit logic used on initial map load AND whenever the result set
    // changes while a map is already showing (previously this only ran once in
    // GoogleMap's onLoad, so re-searching while already in map/both view left the
    // map showing the old area/markers).
    const fitMapToBusinesses = useCallback((mapInstance: google.maps.Map, list: Business[]) => {
        const withCoords = list.filter(b => b.latitude != null && b.longitude != null);
        if (withCoords.length === 0) return;
        const bounds = new google.maps.LatLngBounds();
        withCoords.forEach(b => bounds.extend({ lat: Number(b.latitude), lng: Number(b.longitude) }));
        mapInstance.fitBounds(bounds);
        google.maps.event.addListenerOnce(mapInstance, 'idle', () => {
            // Don't end up zoomed in absurdly close for a single/tightly-clustered result.
            if (mapInstance.getZoom()! > 16) mapInstance.setZoom(16);
        });
    }, []);

    useEffect(() => {
        if (map) {
            fitMapToBusinesses(map, businesses);
        }
    }, [businesses, map, fitMapToBusinesses]);

    // Handle map panning on business selection/hover, debounced so quickly hovering
    // across several list cards (e.g. while scrolling) doesn't fire a burst of
    // overlapping pan animations — only the item the user settles on pans the map.
    useEffect(() => {
        if (!map || !selectedBusiness || selectedBusiness.latitude == null || selectedBusiness.longitude == null) {
            return;
        }

        const newCenter = {
            lat: Number(selectedBusiness.latitude),
            lng: Number(selectedBusiness.longitude)
        };

        const panTimeout = setTimeout(() => {
            // Use panTo for smooth movement; deliberately not forcing zoom on every
            // hover — auto-zooming on every hover was the main source of the map
            // feeling jumpy/finicky. But a pan by itself is imperceptible when the
            // map is still at its zoomed-out "fit all results" level (a few pixels
            // of movement across a whole country/state), so bump the zoom up once,
            // only when it's currently below street level — never zooms back out,
            // so a user who has manually zoomed in further is left alone.
            const MIN_HOVER_ZOOM = 12;
            const currentZoom = map.getZoom();
            if (currentZoom != null && currentZoom < MIN_HOVER_ZOOM) {
                map.setZoom(MIN_HOVER_ZOOM);
            }
            map.panTo(newCenter);

            const idleListener = google.maps.event.addListenerOnce(map, 'idle', () => {
                setCenter(newCenter);
            });

            return () => google.maps.event.removeListener(idleListener);
        }, 200);

        return () => clearTimeout(panTimeout);
    }, [selectedBusiness, map]);

    const handleSearch = () => {
        const params = new URLSearchParams();
        if (searchQuery) params.append('q', searchQuery);
        if (selectedCategory) params.append('categoryId', selectedCategory);
        if (selectedCity) params.append('cityId', selectedCity);
        if (!excludeIran) params.append('includeIran', '1');
        navigate(`/search?${params.toString()}`);
    };

    // Toggling the checkbox re-runs the current search straight away. Built from
    // the current filters (not the address), so it also works on /category/... and
    // /city/... pages, where the filter is not in the query string.
    const handleExcludeIranChange = (checked: boolean) => {
        setExcludeIran(checked);
        const params = new URLSearchParams();
        const currentQuery = new URLSearchParams(location.search).get('q');
        if (currentQuery) params.append('q', currentQuery);
        if (selectedCategory) params.append('categoryId', selectedCategory);
        if (selectedCity) params.append('cityId', selectedCity);
        if (!checked) params.append('includeIran', '1');
        navigate(`/search?${params.toString()}`);
    };

    // /category/<slug> and /city/<slug>: select the matching dropdown entry.
    useEffect(() => {
        if (categorySlug && categories.length > 0) {
            setSelectedCategory(categories.find(c => c.slug.toLowerCase() === categorySlug.toLowerCase())?.id || '');
        }
        if (citySlug && cities.length > 0) {
            setSelectedCity(cities.find(c => cityPathSlug(c) === citySlug || c.slug === citySlug)?.id || '');
        }
    }, [categorySlug, citySlug, categories, cities]);

    // One address per browse page: a search for just a category, or just a city,
    // moves to its readable address (old /search?categoryId=... links included).
    useEffect(() => {
        if (location.pathname !== '/search') return;
        const params = new URLSearchParams(location.search);
        const categoryId = params.get('categoryId');
        const cityId = params.get('cityId');
        if (params.get('q') || params.get('includeIran') || (categoryId && cityId)) return;
        if (categoryId) {
            const category = categories.find(c => c.id === categoryId);
            if (category?.slug) navigate(categoryPath(category), { replace: true });
        } else if (cityId) {
            const city = cities.find(c => c.id === cityId);
            if (city && cityPathSlug(city)) navigate(cityPath(city), { replace: true });
        }
    }, [location.pathname, location.search, categories, cities, navigate]);

    const activeCategory = categories.find(c => c.id === selectedCategory);
    const activeCity = cities.find(c => c.id === selectedCity);
    const activeCategoryName = activeCategory?.name;
    const activeCityName = activeCity ? cityDisplayName(activeCity) : undefined;
    const filterLabelParts = [activeCategoryName, activeCityName].filter(Boolean) as string[];
    // Heading + intro for browse pages. The API sends the same wording in the
    // page head for /category/... and /city/... (see "SEO browse pages" there).
    const browseHeading = activeCategoryName && activeCityName
        ? `${activeCategoryName} در ${activeCityName}`
        : activeCategoryName
            ? `${activeCategoryName} - کسب‌وکارهای ایرانی در استرالیا`
            : activeCityName
                ? `کسب‌وکارهای ایرانی در ${activeCityName}`
                : '';
    const browseIntro = activeCategoryName && activeCityName
        ? `فهرست کسب‌وکارهای ایرانی در دسته «${activeCategoryName}» در ${activeCityName}؛ آدرس، تلفن، ساعات کاری و نظرات کاربران در چیستا.`
        : activeCategoryName
            ? `فهرست کسب‌وکارهای ایرانی در دسته «${activeCategoryName}» در سراسر استرالیا؛ آدرس، تلفن، ساعات کاری و نظرات کاربران در چیستا.`
            : activeCityName
                ? `فهرست کسب‌وکارها و خدمات ایرانی در ${activeCityName}؛ آدرس، تلفن، ساعات کاری و نظرات کاربران در چیستا.`
                : '';
    const seoTitle = browseHeading
        || (searchQuery
            ? `نتایج جستجو برای «${searchQuery}»`
            : 'جستجوی کسب‌وکارهای ایرانی');
    const seoDescription = browseIntro
        || (searchQuery
            ? `نتایج جستجو برای «${searchQuery}» در دایرکتوری کسب‌وکارهای ایرانیان چیستا.`
            : 'کسب‌وکارهای ایرانی استرالیا را بر اساس دسته‌بندی، شهر یا نام جستجو کنید.');
    // Free-text search result pages are unbounded/thin-content (near-infinite query
    // combinations) so they're kept out of the index; category/city browse pages are a
    // finite, valuable set of landing pages (e.g. "Persian restaurants in Sydney") worth indexing.
    // The default canonical drops the query string, which would point every
    // category/city page at plain /search and keep them all out of the index.
    // Keep just the two filters that define the page, in a fixed order.
    const canonicalParams = new URLSearchParams();
    if (selectedCategory) canonicalParams.set('categoryId', selectedCategory);
    if (selectedCity) canonicalParams.set('cityId', selectedCity);
    const canonicalQuery = canonicalParams.toString();
    // A single category or a single city has a readable address; that is the
    // canonical one. Category + city together stays on the query form for now.
    const canonicalPath = activeCategory && !selectedCity
        ? categoryPath(activeCategory)
        : activeCity && !selectedCategory
            ? cityPath(activeCity)
            : canonicalQuery
                ? `/search?${canonicalQuery}`
                : categorySlug
                    ? `/category/${categorySlug}`
                    : citySlug
                        ? `/city/${citySlug}`
                        : '/search';
    // A /category/... or /city/... address that matches nothing must not be indexed.
    const unknownBrowseSlug = (Boolean(categorySlug) && categories.length > 0 && !activeCategory)
        || (Boolean(citySlug) && cities.length > 0 && !activeCity);
    useSEO({
        title: seoTitle,
        description: seoDescription,
        canonical: canonicalPath,
        noindex: (Boolean(searchQuery) && filterLabelParts.length === 0) || unknownBrowseSlug,
    });

    if (loading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mt: 8, gap: 2 }}>
                <CircularProgress />
                <Typography variant="body1" sx={{ color: '#717a8f', direction: 'rtl' }}>
                    لطفاً صبر کنید، در حال دریافت اطلاعات...
                </Typography>
            </Box>
        );
    }

    return (
        <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: { xs: 2, md: 4 } }}>
            {/* Search Bar */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                    mb: 6,
                    p: 2,
                    backgroundColor: '#f5f5f5',
                    borderRadius: 2,
                    boxShadow: 1,
                    // 14px to match the rest of the site. The typed search box
                    // stays 16px on phones: iOS zooms the page on smaller inputs.
                    '& .MuiInputBase-input': { fontSize: { xs: '16px', md: '14px' } },
                    '& .MuiSelect-select': { fontSize: '14px' },
                    '& .MuiInputLabel-root': { fontSize: '14px' },
                    '& .MuiInputLabel-shrink': { fontSize: '16px' },
                    '& .MuiButton-root': { fontSize: '14px' },
                }}
            >
                <TextField
                    placeholder="دنبال چه هستید؟"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{ backgroundColor: 'white', borderRadius: 1 }}
                />
                <TextField
                    select
                    label="دسته‌بندی"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    sx={{ backgroundColor: 'white', borderRadius: 1, minWidth: { md: '200px' } }}
                >
                    <MenuItem value="" sx={{ fontSize: '14px' }}>تمام دسته‌ها</MenuItem>
                    {categories.map((cat) => (
                        <MenuItem key={cat.id} value={cat.id} sx={{ fontSize: '14px' }}>
                            {cat.name}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    label="شهر / منطقه"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={selectedCity}
                    onChange={(e) => setSelectedCity(e.target.value)}
                    sx={{ backgroundColor: 'white', borderRadius: 1, minWidth: { md: '200px' } }}
                >
                    <MenuItem value="" sx={{ fontSize: '14px' }}>تمام استرالیا</MenuItem>
                    {cities.map((city) => (
                        <MenuItem key={city.id} value={city.id} sx={{ fontSize: '14px' }}>
                            {city.name}
                        </MenuItem>
                    ))}
                </TextField>
                <FormControlLabel
                    control={<Checkbox size="small" checked={excludeIran} onChange={(e) => handleExcludeIranChange(e.target.checked)} />}
                    label={
                        <Box component="span" sx={{ fontSize: '0.85rem', direction: 'rtl' }}>
                            عدم نمایش کسب‌وکارهای داخل ایران
                        </Box>
                    }
                    sx={{ m: 0, whiteSpace: 'nowrap', alignSelf: { md: 'center' } }}
                />
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSearch}
                    sx={{ px: 4, fontWeight: 'bold' }}
                >
                    جستجو
                </Button>
            </Box>

            {/* Category / city browse pages get their own visible heading and a
                short intro; a plain search keeps the hidden generic heading below. */}
            {browseHeading && (
                <Box sx={{ direction: 'rtl', textAlign: 'right', mb: 2.5 }}>
                    <Typography component="h1" sx={{ fontSize: { xs: '1.05rem', md: '1.25rem' }, fontWeight: 800, color: '#1a202c', mb: 0.5 }}>
                        {browseHeading}
                    </Typography>
                    <Typography sx={{ fontSize: '14px', color: 'text.secondary', lineHeight: 1.8 }}>
                        {browseIntro}
                    </Typography>
                </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    {!browseHeading && (
                        <Typography variant="h4" component="h1" sx={{
                            // Not shown on screen at any size, but kept in the page
                            // (visually hidden, not display:none) so it still has a
                            // main heading for search engines and screen readers.
                            position: 'absolute',
                            width: '1px',
                            height: '1px',
                            overflow: 'hidden',
                            clipPath: 'inset(50%)',
                            whiteSpace: 'nowrap',
                        }}>
                            نتایج جستجو
                        </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ direction: 'rtl', whiteSpace: 'nowrap', fontSize: '14px' }}>
                        {businesses.length} نتیجه یافت شد
                    </Typography>
                    {viewMode !== 'list' && mappableBusinesses.length !== businesses.length && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', direction: 'rtl' }}>
                            {mappableBusinesses.length} مورد از {businesses.length} روی نقشه ({businesses.length - mappableBusinesses.length} مورد بدون موقعیت ثبت‌شده)
                        </Typography>
                    )}
                </Box>
                <Box sx={{
                    display: 'flex', border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden',
                    '& .MuiButton-root': { fontSize: '13px', py: 0.5, px: 1.75, minHeight: 34 },
                    '& .MuiButton-startIcon > svg': { fontSize: 18 },
                }}>
                    <Button
                        variant={viewMode === 'list' ? 'contained' : 'text'}
                        onClick={() => setViewMode('list')}
                        startIcon={<ViewListIcon />}
                        sx={{ borderRadius: 0 }}
                    >
                        لیست
                    </Button>
                    <Button
                        variant={viewMode === 'map' ? 'contained' : 'text'}
                        onClick={() => setViewMode('map')}
                        startIcon={<MapIcon />}
                        sx={{ borderRadius: 0 }}
                    >
                        نقشه
                    </Button>
                    <Button
                        variant={viewMode === 'both' ? 'contained' : 'text'}
                        onClick={() => setViewMode('both')}
                        startIcon={<ViewCompactIcon />}
                        sx={{ display: { xs: 'none', md: 'inline-flex' }, borderRadius: 0, borderLeft: '1px solid #e0e0e0' }}
                    >
                        هر دو
                    </Button>
                </Box>
            </Box>

            {viewMode === 'list' ? (
                <Grid container spacing={3}>
                    {businesses.slice(0, visibleCount).map((business) => (
                        <Grid key={business.id} size={{ xs: 12, sm: 6, md: 4 }}>
                            <BusinessCard business={business} />
                        </Grid>
                    ))}
                    {businesses.length > visibleCount && (
                        <Grid size={{ xs: 12 }}>
                            <Box sx={{ textAlign: 'center', mt: 1 }}>
                                <Button
                                    variant="outlined"
                                    size="large"
                                    onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                                    sx={{ px: 5, fontWeight: 'bold', direction: 'rtl' }}
                                >
                                    نمایش بیشتر ({businesses.length - visibleCount} مورد دیگر)
                                </Button>
                            </Box>
                        </Grid>
                    )}
                    {businesses.length === 0 && (
                        <Grid size={{ xs: 12 }}>
                            <Box sx={{ p: 4, textAlign: 'center', bgcolor: 'background.paper', borderRadius: 1 }}>
                                <Typography variant="h6" color="text.secondary">
                                    No businesses found matching your criteria.
                                </Typography>
                                <Button variant="outlined" sx={{ mt: 2 }} onClick={() => navigate('/')}>
                                    Clear Filters
                                </Button>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            ) : viewMode === 'map' ? (
                <Box sx={{ height: '70vh', width: '100%', borderRadius: 2, overflow: 'hidden', border: '1px solid #e0e0e0', mt: 2 }}>
                    {isLoaded && (
                        <GoogleMap
                            mapContainerStyle={{ width: '100%', height: '100%' }}
                            center={center}
                            zoom={12}
                            onLoad={(mapInstance) => {
                                setMap(mapInstance);
                                fitMapToBusinesses(mapInstance, businesses);
                            }}
                            onUnmount={() => setMap(null)}
                            onDragEnd={() => {
                                if (map) setCenter(map.getCenter()!.toJSON());
                            }}
                            onZoomChanged={() => {
                                if (map) setCenter(map.getCenter()!.toJSON());
                            }}
                        >
                            <MarkerClusterer styles={clusterStyles}>
                                {(clusterer) => (
                                    <>
                                        {businesses.map((business) => (
                                            business.latitude != null && business.longitude != null && (
                                                <Marker
                                                    key={business.id}
                                                    position={{ lat: Number(business.latitude), lng: Number(business.longitude) }}
                                                    clusterer={clusterer}
                                                    icon={selectedBusiness?.id === business.id ? selectedMarkerIcon : markerIcon}
                                                    zIndex={selectedBusiness?.id === business.id ? 1000 : 1}
                                                    onMouseOver={() => setSelectedBusiness(business)}
                                                    onClick={() => navigate(`/business/${business.slug || business.id}`)}
                                                />
                                            )
                                        ))}
                                    </>
                                )}
                            </MarkerClusterer>

                            {selectedBusiness && selectedBusiness.latitude != null && selectedBusiness.longitude != null && (
                                <InfoWindow
                                    position={{ lat: Number(selectedBusiness.latitude), lng: Number(selectedBusiness.longitude) }}
                                    onCloseClick={() => setSelectedBusiness(null)}
                                >
                                    <Box
                                        sx={{
                                            p: 0.8,
                                            minWidth: 210,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            flexDirection: 'column'
                                        }}
                                        onClick={() => navigate(`/business/${selectedBusiness.slug || selectedBusiness.id}`)}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1, textAlign: 'left', direction: 'ltr' }}>
                                            <Avatar
                                                src={
                                                    (selectedBusiness.logoUrl && selectedBusiness.logoUrl.startsWith('/uploads'))
                                                        ? `${API_ORIGIN}${selectedBusiness.logoUrl}`
                                                        : undefined
                                                }
                                                alt={selectedBusiness.name}
                                                sx={{
                                                    width: 42,
                                                    height: 42,
                                                    border: '1px solid #eee',
                                                    bgcolor: 'white',
                                                    mr: 1.5
                                                }}
                                            >
                                                {!selectedBusiness.logoUrl && <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>{selectedBusiness.name.substring(0, 2).toUpperCase()}</Typography>}
                                            </Avatar>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold', lineHeight: 1.2, mb: 0.3, color: '#2854a1' }}>
                                                    {selectedBusiness.name}
                                                </Typography>

                                                {selectedBusiness.category?.name && (
                                                    <Typography variant="caption" sx={{ display: 'block', color: '#64748b', fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', mb: 0.8, letterSpacing: '0.5px' }}>
                                                        {selectedBusiness.category.name}
                                                    </Typography>
                                                )}
                                                
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                                                    <FmdGoodOutlinedIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                                                    <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 500 }}>
                                                        {[selectedBusiness.address, selectedBusiness.city?.name].filter(val => val && val !== 'Address TBD').join(', ') || 'Address TBD'}
                                                    </Typography>
                                                </Box>
                                                
                                                {selectedBusiness.phone && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <PhoneOutlinedIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                                                        <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 500 }}>
                                                            {selectedBusiness.phone}
                                                        </Typography>
                                                    </Box>
                                                )}

                                                {selectedBusiness.googlePlaceId && selectedBusiness.googleRating && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.8, pt: 0.5, borderTop: '1px solid #f1f5f9' }}>
                                                        <Box component="img" src="https://www.gstatic.com/images/branding/product/1x/googleg_32dp.png" sx={{ height: 12 }} />
                                                        <Rating 
                                                            value={selectedBusiness.googleRating} 
                                                            readOnly 
                                                            size="small" 
                                                            precision={0.1}
                                                            sx={{ fontSize: '0.7rem', color: '#faaf00' }} 
                                                        />
                                                        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#64748b' }}>
                                                            {selectedBusiness.googleRating.toFixed(1)}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                </InfoWindow>
                            )}
                        </GoogleMap>
                    )}
                </Box>
            ) : (
                <Grid container spacing={0} sx={{ height: '80vh', border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden', mt: 2 }}>
                    {/* Map on the Left (7/12) */}
                    <Grid size={{ xs: 12, md: 7 }} sx={{ height: '100%', position: 'relative' }}>
                        {isLoaded && (
                            <GoogleMap
                                mapContainerStyle={{ width: '100%', height: '100%' }}
                                center={center}
                                zoom={12}
                                onLoad={(mapInstance) => {
                                    setMap(mapInstance);
                                    fitMapToBusinesses(mapInstance, businesses);
                                }}
                                onUnmount={() => setMap(null)}
                                onDragEnd={() => {
                                    if (map) {
                                        setCenter(map.getCenter()!.toJSON());
                                    }
                                }}
                                onZoomChanged={() => {
                                    if (map) {
                                        // Update center when zooming too, to keep it in sync
                                        setCenter(map.getCenter()!.toJSON());
                                    }
                                }}
                            >
                                <MarkerClusterer styles={clusterStyles}>
                                    {(clusterer) => (
                                        <>
                                            {businesses.map((business) => (
                                                business.latitude != null && business.longitude != null && (
                                                    <Marker
                                                        key={business.id}
                                                        position={{ lat: Number(business.latitude), lng: Number(business.longitude) }}
                                                        clusterer={clusterer}
                                                        icon={selectedBusiness?.id === business.id ? selectedMarkerIcon : markerIcon}
                                                        zIndex={selectedBusiness?.id === business.id ? 1000 : 1}
                                                        onMouseOver={() => {
                                                            setSelectedBusiness(business);
                                                        }}
                                                        onClick={() => navigate(`/business/${business.slug || business.id}`)}
                                                    />
                                                )
                                            ))}
                                        </>
                                    )}
                                </MarkerClusterer>

                                {selectedBusiness && selectedBusiness.latitude != null && selectedBusiness.longitude != null && (
                                    <InfoWindow
                                        position={{ lat: Number(selectedBusiness.latitude), lng: Number(selectedBusiness.longitude) }}
                                        onCloseClick={() => setSelectedBusiness(null)}
                                    >
                                        <Box
                                            sx={{
                                                p: 0.8,
                                                minWidth: 210,
                                                cursor: 'pointer',
                                                display: 'flex',
                                                flexDirection: 'column'
                                            }}
                                            onClick={() => navigate(`/business/${selectedBusiness.slug || selectedBusiness.id}`)}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1, textAlign: 'left', direction: 'ltr' }}>
                                                <Avatar
                                                    src={
                                                        (selectedBusiness.logoUrl && selectedBusiness.logoUrl.startsWith('/uploads'))
                                                            ? `${API_ORIGIN}${selectedBusiness.logoUrl}`
                                                            : undefined
                                                    }
                                                    alt={selectedBusiness.name}
                                                    sx={{
                                                        width: 42,
                                                        height: 42,
                                                        border: '1px solid #eee',
                                                        bgcolor: 'white',
                                                        mr: 1.5
                                                    }}
                                                >
                                                    {!selectedBusiness.logoUrl && <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>{selectedBusiness.name.substring(0, 2).toUpperCase()}</Typography>}
                                                </Avatar>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold', lineHeight: 1.2, mb: 0.3, color: '#2854a1' }}>
                                                        {selectedBusiness.name}
                                                    </Typography>

                                                    {selectedBusiness.category?.name && (
                                                        <Typography variant="caption" sx={{ display: 'block', color: '#64748b', fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', mb: 0.8, letterSpacing: '0.5px' }}>
                                                            {selectedBusiness.category.name}
                                                        </Typography>
                                                    )}
                                                    
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                                                        <FmdGoodOutlinedIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                                                        <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 500 }}>
                                                            {[selectedBusiness.address, selectedBusiness.city?.name].filter(val => val && val !== 'Address TBD').join(', ') || 'Address TBD'}
                                                        </Typography>
                                                    </Box>
                                                    
                                                    {selectedBusiness.phone && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <PhoneOutlinedIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                                                            <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 500 }}>
                                                                {selectedBusiness.phone}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </InfoWindow>
                                )}
                            </GoogleMap>
                        )}
                    </Grid>

                    {/* Scrollable List on the Right (5/12) */}
                    <Grid size={{ xs: 12, md: 5 }} sx={{ height: '100%', overflowY: 'auto', borderLeft: '1px solid #e0e0e0', bgcolor: '#f8f9fa' }}>
                        <Box sx={{ p: 3 }}>
                            <Grid container spacing={2}>
                                {businesses.map((business) => (
                                    <Grid key={business.id} size={{ xs: 12 }}>
                                        <Card
                                            sx={{
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                cursor: 'pointer',
                                                borderRadius: 1.5,
                                                position: 'relative',
                                                overflow: 'hidden',
                                                boxShadow: selectedBusiness?.id === business.id ? 6 : 2,
                                                border: selectedBusiness?.id === business.id ? '2px solid #2854a1' : '2px solid transparent',
                                                transition: 'all 0.2s ease-in-out',
                                                '&:hover': {
                                                    boxShadow: 8,
                                                    transform: 'translateY(-2px)'
                                                }
                                            }}
                                            onMouseEnter={() => setSelectedBusiness(business)}
                                            onClick={() => navigate(`/business/${business.slug || business.id}`)}
                                        >
                                            {/* Card Image Overlay Style */}
                                            {/* Same fit-entire-photo treatment as BusinessCard/BusinessDetailsPage:
                                                object-fit: cover here regularly cropped heads/logos out of frame,
                                                so show the whole photo (contain) over a blurred fill of itself. */}
                                            <Box sx={{ position: 'relative', height: 160, width: '100%', overflow: 'hidden', bgcolor: '#111827' }}>
                                                <Box
                                                    component="img"
                                                    src={
                                                        (business.mainImage && business.mainImage.startsWith('/uploads'))
                                                            ? `${API_ORIGIN}${business.mainImage}`
                                                            : (business.logoUrl && business.logoUrl.startsWith('/uploads'))
                                                                ? `${API_ORIGIN}${business.logoUrl}`
                                                                : '/placeholder-business.jpg'
                                                    }
                                                    alt=""
                                                    aria-hidden="true"
                                                    loading="lazy"
                                                    decoding="async"
                                                    sx={{
                                                        position: 'absolute',
                                                        inset: 0,
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                        filter: 'blur(16px) brightness(0.55)',
                                                        transform: 'scale(1.15)',
                                                    }}
                                                />
                                                <CardMedia
                                                    component="img"
                                                    height="100%"
                                                    image={
                                                        (business.mainImage && business.mainImage.startsWith('/uploads'))
                                                            ? `${API_ORIGIN}${business.mainImage}`
                                                            : (business.logoUrl && business.logoUrl.startsWith('/uploads'))
                                                                ? `${API_ORIGIN}${business.logoUrl}`
                                                                : '/placeholder-business.jpg'
                                                    }
                                                    alt={business.name}
                                                    loading="lazy"
                                                    decoding="async"
                                                    sx={{ position: 'relative', width: '100%', height: '100%', objectFit: 'contain' }}
                                                />
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        bottom: 0,
                                                        left: 0,
                                                        right: 0,
                                                        height: '70%',
                                                        background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 100%)',
                                                        zIndex: 1
                                                    }}
                                                />
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        bottom: 10,
                                                        left: 10,
                                                        right: 10,
                                                        zIndex: 2,
                                                        color: 'white'
                                                    }}
                                                >
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '0.95rem', lineHeight: 1.2 }}>
                                                        {business.name}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <CardContent sx={{ p: 1.5, flexGrow: 1, bgcolor: 'white' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                    {business.category?.name && (
                                                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                            {business.category.name}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                                                    <FmdGoodOutlinedIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                                                    <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                                                        {[business.address, business.city?.name].filter(val => val && val !== 'Address TBD').join(', ') || 'Address TBD'}
                                                    </Typography>
                                                </Box>
                                                {business.phone && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                                        <PhoneOutlinedIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                                                            {business.phone}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {business.googlePlaceId && business.googleRating && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, pt: 0.5, borderTop: '1px solid #f1f5f9' }}>
                                                        <Box component="img" src="https://www.gstatic.com/images/branding/product/1x/googleg_32dp.png" sx={{ height: 12 }} />
                                                        <Rating 
                                                            value={business.googleRating} 
                                                            readOnly 
                                                            size="small" 
                                                            precision={0.1}
                                                            sx={{ fontSize: '0.75rem', color: '#faaf00' }} 
                                                        />
                                                        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#64748b' }}>
                                                            {business.googleRating.toFixed(1)}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                            {businesses.length === 0 && (
                                <Box sx={{ p: 4, textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        No results found.
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            )}
        </Container>
    );
};

export default SearchResultsPage;
